import React, { useCallback } from 'react';
import TeamProfileCardTrigger from '@atlaskit/profilecard/team';
import { useFlagsService, AkFlag, toFlagId } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useNavigateToTeamsApp } from '@atlassian/jira-teams-app-config/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { generateUserLink } from '../../common/utils.tsx';
import { useProfileClient } from '../../controllers/profileclient/index.tsx';
import type { TeamProfileCardNextProps } from './types.tsx';

type InnerProps = TeamProfileCardNextProps & {
	userId: string;
	onRedirect: (url: string) => void;
};

export const TeamProfileCardInner = ({
	children,
	onRedirect,
	position,
	shouldRenderToParent,
	teamId,
	trigger,
	triggerLinkType,
	userId,
}: InnerProps) => {
	const { showFlag, dismissFlag } = useFlagsService();

	const onUserClick = useCallback(
		(accountId: string) => onRedirect(generateUserLink(accountId)),
		[onRedirect],
	);

	const { href, onNavigate } = useNavigateToTeamsApp({
		type: 'TEAM',
		payload: { teamId },
	});

	const teamProfileUrl = fg('jira-adopt-teams-nav-config') ? href : `/jira/people/team/${teamId}`;

	const { data: orgId } = useOrgId();

	const { profileClient } = useProfileClient();

	if (!profileClient) {
		return <>{children}</>;
	}

	return (
		<TeamProfileCardTrigger
			teamId={teamId}
			orgId={orgId}
			viewingUserId={userId}
			resourceClient={profileClient}
			trigger={trigger}
			triggerLinkType={triggerLinkType}
			viewProfileLink={teamProfileUrl}
			viewProfileOnClick={() =>
				fg('jira-adopt-teams-nav-config') ? onNavigate() : onRedirect(teamProfileUrl)
			}
			generateUserLink={generateUserLink}
			onUserClick={onUserClick}
			position={position}
			shouldRenderToParent={shouldRenderToParent}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- FYI: Typed as "any" in platform component
			addFlag={(flag: any) => {
				const flagId = toFlagId(flag.id);
				showFlag({
					id: flagId,
					render: (flagProps: FlagCustomRenderProps) => (
						<AkFlag
							{...flag}
							onDismissed={() => {
								dismissFlag(flagId);
							}}
							messageId={`profilecard-next.ui.team-profilecard.ak-flag.${flagId}`}
							messageType="transactional"
							{...flagProps}
						/>
					),
				});
			}}
		>
			{children}
		</TeamProfileCardTrigger>
	);
};
